import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDropzone } from "react-dropzone";
import {
  Upload,
  FileText,
  Trash2,
  RefreshCw,
  Search,
  Filter,
  MoreVertical,
  Download,
  Eye,
  CheckCircle,
  AlertCircle,
  Clock,
  Database,
  MessageSquare,
  Send,
  Bot,
  User,
  Sparkles,
  Brain,
  X,
  ChevronDown,
  Check,
  Info,
  Building2,
  Globe,
  Lock,
  Pencil,
  Plus,
  Save,
  Network,
} from "lucide-react";
import { useKnowledge } from "../context/KnowledgeContext";
import {
  useSuccessToast,
  useErrorToast,
  useInfoToast,
} from "@foundation/altzero_base/base/components/ui/toast";
import { Progress } from "@foundation/altzero_base/base/components/ui/progress";
import { knowledgeService } from "../services/knowledgeService";
import { DocumentStatus, DocumentVisibility, ChunkingStrategy, ChunkingStrategyInfo, ChunkingRecommendation } from "../types/knowledge";
import { GraphExplorer } from "./GraphExplorer";
import ReactMarkdown from "react-markdown";
import { KBSettings } from "./KBSettings";
import {
  KNOWLEDGE_TOAST_MESSAGES,
  KNOWLEDGE_ENDPOINTS,
  DEFAULT_SYSTEM_MESSAGE,
} from "../utils/constants";
// Import from the main app's foundation (same instance as App.tsx)
// Must use the main foundation UserContext, not the local copy in knowledgebase/foundation
import { useUser } from "@foundation/altzero_base/base/contextapi/UserContext";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  sources?: any[];
  metadata?: {
    uniqueDocuments?: number;
    documentsUsed?: string[];
    retrievedDocuments?: number;
    processingTime?: number;
    model?: string;
    hasValidContext?: boolean;
  };
  retrieval?: 'golden' | 'hybrid+golden' | 'standard' | 'graph' | 'deep' | 'page-index';
  goldenMatch?: {
    id: string;
    similarity: number;
    question: string;
    confidence: number;
  };
  graphContext?: {
    entitiesFound: number;
    relationshipsTraversed: number;
    graphChunksAdded: number;
  };
  ragChunks?: number;
  pageIndexSections?: number;
  streaming?: boolean;
}

const AIAssistant: React.FC = () => {
  const { user, isLoading: userLoading } = useUser();
  const {
    state: { documents, uploadProgress, stats, isUploading, error },
    setDocuments,
    addDocument,
    setUploadProgress,
    clearUploadProgress,
    setStats,
    setUploading,
    setError,
    clearError,
    updateDocument,
    deleteDocument: removeDocument,
  } = useKnowledge();

  // Get orgId using the same approach as QuickCompare (RPC function)
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [isOrgIdLoading, setIsOrgIdLoading] = useState<boolean>(true);
  const hasLoadedDocumentsRef = useRef(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [pipelineFilter, setPipelineFilter] = useState<'all' | 'graph_processing' | 'graph_failed' | 'index_processing' | 'index_completed' | 'index_failed'>('all');
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | "all">(
    "all"
  );
  const [visibilityFilter, setVisibilityFilter] = useState<DocumentVisibility | "all">("all");
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"upload" | "chat" | "graph" | "settings">("upload");
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [graphDocFilter, setGraphDocFilter] = useState<string | null>(null);
  const graphEnabled = knowledgeService.isGraphEnabled();

  // Upload form state
  const [documentDescription, setDocumentDescription] =
    useState<string>("");
  const [documentCategory, setDocumentCategory] = useState<string>("");
  const [documentSubCategory, setDocumentSubCategory] = useState<string>("");
  const [parseTier, setParseTier] = useState<'fast' | 'cost_effective' | 'agentic' | 'agentic_plus'>('cost_effective');
  const [parseLanguage, setParseLanguage] = useState<string>('en');
  const [parsingInstructions, setParsingInstructions] = useState<string>("");
  // Chunking strategy state
  const [chunkingStrategy, setChunkingStrategy] = useState<ChunkingStrategy>('fixed');
  // Page index opt-in
  const [buildPageIndex, setBuildPageIndex] = useState(true);
  const [chunkingStrategies, setChunkingStrategies] = useState<ChunkingStrategyInfo[]>([]);
  const [chunkingRecommendation, setChunkingRecommendation] = useState<ChunkingRecommendation | null>(null);
  const [isRecommending, setIsRecommending] = useState(false);
  // AI analysis state (tags + ontology)
  const [documentTags, setDocumentTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [recommendedOntology, setRecommendedOntology] = useState<import('../types/knowledge').RecommendedOntology | null>(null);
  const [showOntologyEditor, setShowOntologyEditor] = useState(false);
  // Ontology mode: 'static' = preconfigured only, 'hybrid' = preconfigured + AI append, 'dynamic' = AI only
  const [ontologyMode, setOntologyMode] = useState<'static' | 'hybrid' | 'dynamic'>('hybrid');
  const [staticOntologies, setStaticOntologies] = useState<Array<{
    name: string; domain: string; description: string; isDefault: boolean;
    entityTypeCount: number; relationshipTypeCount: number;
    entityTypes: Array<{ name: string; description: string; properties: string[] }>;
    relationshipTypes: Array<{ name: string; fromType: string; toType: string; description: string }>;
  }>>([]);
  const [selectedStaticOntology, setSelectedStaticOntology] = useState<string>('default');
  // Categories loaded from DB for subcategory dropdown
  const [loadedCategories, setLoadedCategories] = useState<Array<{ name: string; subcategories: string[] }>>([]);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [fileCustomNames, setFileCustomNames] = useState<string[]>([]);
  const [uploadCancelled, setUploadCancelled] = useState(false);
  const [uploadController, setUploadController] = useState<AbortController | null>(null);
  const [autoCloseTimer, setAutoCloseTimer] = useState<NodeJS.Timeout | null>(null);
  
  // Upload provider selection (can select both)
  // Default to only 'knowledge' — Gemini is added once the store is confirmed available
  const [uploadProviders, setUploadProviders] = useState<Set<RAGProvider>>(() => {
    return new Set(['knowledge']);
  });

  // Document visibility: 'private' = uploader only, 'org' = org members, 'global' = all users
  const [docVisibility, setDocVisibility] = useState<'private' | 'org' | 'global'>('private');
  // Role checks for visibility gating
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  // Provider selection (similar to Quick Compare)
  type RAGProvider = 'knowledge' | 'gemini';
  const getProviderKey = () => {
    if (typeof window !== 'undefined' && user?.email) {
      const sanitizedEmail = user.email.replace(/[^a-zA-Z0-9]/g, '_');
      return `knowledgeBase_provider_${sanitizedEmail}`;
    }
    return 'knowledgeBase_provider_default';
  };

  const getStoreIdKey = () => {
    if (typeof window !== 'undefined' && user?.email) {
      const sanitizedEmail = user.email.replace(/[^a-zA-Z0-9]/g, '_');
      return `knowledgeBase_storeId_${sanitizedEmail}`;
    }
    return 'knowledgeBase_storeId_default';
  };

  const getStoreName = () => {
    if (user?.email) {
      return `Knowledge Base - ${user.email}`;
    }
    return 'Knowledge Base';
  };

  const [provider, setProvider] = useState<RAGProvider>(() => {
    if (typeof window !== 'undefined' && user?.email) {
      const saved = localStorage.getItem(getProviderKey());
      return (saved as RAGProvider) || 'knowledge';
    }
    return 'knowledge';
  });

  // Store ID for Gemini provider (similar to Quick Compare)
  const [geminiStoreId, setGeminiStoreId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(getStoreIdKey());
    }
    return null;
  });

  // Gemini files state
  const [geminiFiles, setGeminiFiles] = useState<Array<{ name: string; category?: string }>>([]);
  const [isLoadingGeminiFiles, setIsLoadingGeminiFiles] = useState(false);

  // Store provider preference
  useEffect(() => {
    if (user?.email) {
      localStorage.setItem(getProviderKey(), provider);
    }
  }, [provider, user?.email]);

  // Create/get Gemini store when provider is Gemini
  useEffect(() => {
    const ensureGeminiStore = async () => {
      if (provider === 'gemini' && !geminiStoreId && user?.email && orgId) {
        try {
          // Create store using Quick Compare endpoint
          const response = await fetch('/api/revisr/quick-compare/create-store', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-user-id': user.id,
              'x-org-id': orgId,
            },
            body: JSON.stringify({
              displayName: getStoreName(),
              ragService: 'gemini',
            }),
          });

          if (response.ok) {
            const data = await response.json();
            setGeminiStoreId(data.storeName);
            localStorage.setItem(getStoreIdKey(), data.storeName);
            console.log('✅ Gemini store created/retrieved:', data.storeName);
          } else {
            console.error('Failed to create Gemini store');
          }
        } catch (error) {
          console.error('Error creating Gemini store:', error);
        }
      }
    };

    ensureGeminiStore();
  }, [provider, geminiStoreId, user?.email, user?.id, orgId]);

  // Load available chunking strategies on mount
  useEffect(() => {
    const loadStrategies = async () => {
      try {
        const strategies = await knowledgeService.getChunkingStrategies();
        setChunkingStrategies(strategies);
      } catch (err) {
        console.warn('Failed to load chunking strategies:', err);
        // Fallback: hardcoded list
        setChunkingStrategies([
          { name: 'fixed', description: 'Fixed-size chunks with overlap', bestFor: 'General documents' },
          { name: 'semantic', description: 'Groups by embedding similarity', bestFor: 'Long narratives' },
          { name: 'hierarchical', description: 'Splits by headings', bestFor: 'Structured docs with sections' },
          { name: 'proposition', description: 'LLM extracts atomic facts', bestFor: 'Medical protocols, guidelines' },
        ]);
      }
    };
    loadStrategies();

    // Load categories for subcategory dropdown
    knowledgeService.getCategories().then(({ categories }) => {
      setLoadedCategories(categories.map((c: { name: string; subcategories?: string[] }) => ({
        name: c.name,
        subcategories: c.subcategories || [],
      })));
    }).catch(() => {});

    // Load static ontologies if graph is enabled
    if (graphEnabled) {
      knowledgeService.getStaticOntologies().then(({ ontologies, mode }) => {
        setStaticOntologies(ontologies);
        if (ontologies.length > 0) {
          setOntologyMode('hybrid');
          const defaultOnt = ontologies.find(o => o.isDefault) || ontologies[0];
          setSelectedStaticOntology(defaultOnt.name);
          setRecommendedOntology({
            entityTypes: defaultOnt.entityTypes,
            relationshipTypes: defaultOnt.relationshipTypes,
          });
        } else {
          setOntologyMode('dynamic');
        }
      }).catch(() => {
        setOntologyMode('dynamic');
      });
    }
  }, []);

  // When static ontology selection changes, load its types
  useEffect(() => {
    if ((ontologyMode === 'static' || ontologyMode === 'hybrid') && selectedStaticOntology) {
      const ont = staticOntologies.find(o => o.name === selectedStaticOntology);
      if (ont) {
        setRecommendedOntology({
          entityTypes: ont.entityTypes,
          relationshipTypes: ont.relationshipTypes,
        });
      }
    }
  }, [selectedStaticOntology, ontologyMode, staticOntologies]);

  // Chat state
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [showChatSettings, setShowChatSettings] = useState(false);
  const [chatTemperature, setChatTemperature] = useState<number>(0.7);
  const [chatMaxTokens, setChatMaxTokens] = useState<number>(2048);
  const [searchMode, setSearchMode] = useState<'fast' | 'semantic' | 'hybrid' | 'page-index' | 'deep'>('hybrid');
  const [chatTopK, setChatTopK] = useState<number>(30);
  const [chatMinScore, setChatMinScore] = useState<number>(0.2);

  const showSuccess = useSuccessToast();
  const showError = useErrorToast();
  const showInfo = useInfoToast();

  // Fetch files from Gemini store (defined after toast hooks)
  const fetchGeminiFiles = useCallback(async () => {
    if (provider !== 'gemini' || !geminiStoreId || !user?.id || !orgId) {
      showError('Cannot fetch files', 'Gemini provider and store ID are required');
      return;
    }

    try {
      setIsLoadingGeminiFiles(true);
      const encodedStoreId = encodeURIComponent(geminiStoreId);
      const endpoint = `/api/revisr/quick-compare/store/${encodedStoreId}/files?ragService=gemini`;
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'x-user-id': user.id,
          'x-org-id': orgId,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch files: ${response.statusText}`);
      }

      const data = await response.json();
      const files = data.files || [];
      setGeminiFiles(files);
      showSuccess('Files fetched', `Found ${files.length} file(s) in Gemini store`);
    } catch (error) {
      console.error('Error fetching Gemini files:', error);
      showError('Failed to fetch files', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsLoadingGeminiFiles(false);
    }
  }, [provider, geminiStoreId, user?.id, orgId, showSuccess, showError]);

  // Cancel upload function
  const cancelUpload = useCallback(() => {
    if (uploadController) {
      uploadController.abort();
      setUploadController(null);
    }
    setUploadCancelled(true);
    setUploading(false);

    // Clear all upload progress
    if (uploadProgress) {
      Object.keys(uploadProgress).forEach((id) => {
        clearUploadProgress(id);
      });
    }

    showInfo("Upload cancelled", "File upload has been cancelled");

    // Reset form state
    setDocumentDescription("");
    setUploadCancelled(false);
  }, [uploadController, uploadProgress, clearUploadProgress, showInfo]);

  // Close dialog function
  const closeDialog = useCallback(() => {
    // Clear auto-close timer if it exists
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      setAutoCloseTimer(null);
    }

    // Cancel any ongoing uploads
    if (isUploading) {
      cancelUpload();
    }

    // Clear upload progress after a short delay
    setTimeout(() => {
      if (uploadProgress) {
        Object.keys(uploadProgress).forEach((id) => {
          clearUploadProgress(id);
        });
      }
    }, 500);

    setStagedFiles([]);
    setChunkingRecommendation(null);
    setShowUploadDialog(false);
  }, [autoCloseTimer, isUploading, cancelUpload, uploadProgress, clearUploadProgress]);

  // Auto-close dialog after successful upload
  const scheduleAutoClose = useCallback(() => {
    const timer = setTimeout(() => {
      showSuccess("Upload completed", "All files uploaded successfully!");
      setTimeout(() => {
        closeDialog();
      }, 1500); // 1.5 second delay after success message
    }, 1000); // 1 second delay before showing success message

    setAutoCloseTimer(timer);
  }, [showSuccess, closeDialog]);

  // Get orgId via backend endpoint (uses service role — avoids RPC permission issues)
  useEffect(() => {
    const getOrgId = async () => {
      if (!user?.id) {
        setIsOrgIdLoading(false);
        return;
      }

      setIsOrgIdLoading(true);
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const apiKey = import.meta.env.VITE_API_KEY || '';
        const response = await fetch(`${apiUrl}${KNOWLEDGE_ENDPOINTS.USER_ORG_ID}`, {
          headers: {
            'x-user-id': user.id,
            'x-api-key': apiKey,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.orgId) {
            setOrgId(data.orgId);
            console.log('✅ Knowledge Base: Got orgId from backend:', data.orgId);
          } else {
            console.warn('Backend returned no orgId for user:', user.id);
          }
        } else {
          console.warn('Could not get orgId from backend:', response.status, await response.text());
        }
      } catch (err) {
        console.warn('Could not get orgId:', err);
      } finally {
        setIsOrgIdLoading(false);
      }
    };

    getOrgId();
  }, [user?.id]);

  // Check user roles for visibility gating
  useEffect(() => {
    if (!user?.id || !orgId) return;
    const checkRoles = async () => {
      try {
        const apiUrl = knowledgeService.getApiUrl();
        const apiKey = knowledgeService.getApiKey();
        // Check org admin via existing role endpoint
        const roleRes = await fetch(`${apiUrl}/api/knowledge/user-role`, {
          headers: { 'x-user-id': user.id, 'x-org-id': orgId, 'x-api-key': apiKey },
        });
        if (roleRes.ok) {
          const roleData = await roleRes.json();
          console.log('🔑 Knowledge Base: user-role response', roleData);
          setIsOrgAdmin(roleData.isOrgAdmin || roleData.isPlatformAdmin || false);
          setIsPlatformAdmin(roleData.isPlatformAdmin || false);
        } else {
          console.warn('🔑 Knowledge Base: user-role failed', roleRes.status, await roleRes.text().catch(() => ''));
        }
      } catch (err) {
        console.warn('🔑 Knowledge Base: user-role error', err);
      }
    };
    checkRoles();
  }, [user?.id, orgId]);

  // Define loadDocuments BEFORE the useEffect that uses it
  const loadDocuments = useCallback(async (): Promise<void> => {
    // Don't load if user is not authenticated or orgId is not available
    if (!user) {
      console.warn("⚠️ Cannot load documents: User not authenticated");
      return;
    }
    if (!orgId) {
      console.warn("⚠️ Cannot load documents: orgId not available");
      return;
    }

    try {
      setIsLoading(true);
      console.log("🔍 Loading documents for user:", user.id, "orgId:", orgId);

      const docs = await knowledgeService.getDocuments(undefined, undefined, orgId, user.id, undefined);
      console.log("📄 Received documents:", docs);

      // Ensure docs is always an array and has proper structure
      const validDocs = Array.isArray(docs)
        ? docs.filter(
            (doc) => doc && typeof doc === "object" && doc.id && doc.name
          )
        : [];

      console.log("✅ Valid documents:", validDocs.length);
      setDocuments(validDocs);
    } catch (error) {
      console.error("❌ Failed to load documents:", error);
      showError(
        "Failed to load documents",
        "Please refresh the page and try again."
      );
      // Set empty array on error
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, orgId, setDocuments, showError]);

  // Load documents function (can be called manually for refresh)
  const loadDocumentsManually = useCallback(async () => {
    // Reset ref to allow reload
    hasLoadedDocumentsRef.current = false;
    
    // Wait for user to be available before loading
    if (!user) {
      console.warn("⚠️ Cannot load documents: User not authenticated");
      return;
    }
    
    // Wait for orgId to be available (needed for Supabase RLS filtering)
    if (!orgId) {
      console.warn("⚠️ Cannot load documents: orgId not available");
      return;
    }
    
    try {
      setIsLoading(true);
      console.log("🔍 Loading documents for user:", user.id, "orgId:", orgId);

      const response = await knowledgeService.getDocuments(undefined, undefined, orgId, user.id, undefined);
      console.log("📄 Received documents response:", response);

      // Backend returns {documents: Array, total: number}, extract documents array
      const docs = (response && typeof response === 'object' && 'documents' in response)
        ? (response as { documents: any[]; total: number }).documents
        : Array.isArray(response)
        ? response
        : [];

      // Ensure docs is always an array and has proper structure
      // Map backend format {name, category} to frontend Document format
      // Use doc.name as the ID since that's what the database uses for identification
      const validDocs = docs
        .filter((doc) => doc && typeof doc === "object" && doc.name)
        .map((doc, index) => ({
          id: doc.name, // Use document name as ID (matches database doc_name)
          name: doc.name,
          type: doc.type || "application/pdf",
          status: doc.status || "success" as DocumentStatus,
          uploadedAt: doc.uploadedAt || new Date().toISOString(),
          size: doc.size || 0,
          category: doc.category,
          ...doc, // Include any other properties
        }));

      console.log("✅ Valid documents:", validDocs.length);
      setDocuments(validDocs);
      hasLoadedDocumentsRef.current = true;
    } catch (error) {
      console.error("❌ Failed to load documents:", error);
      showError(
        "Failed to load documents",
        "Please refresh the page and try again."
      );
      // Set empty array on error
      setDocuments([]);
      // Reset ref on error so user can retry
      hasLoadedDocumentsRef.current = false;
    } finally {
      setIsLoading(false);
    }
  }, [user, orgId, setDocuments, showError]);

  // Load documents when user and orgId are available (same pattern as QuickCompare)
  useEffect(() => {
    const loadDocumentsOnce = async () => {
      // Wait for user to be available before loading
      if (!user) {
        return;
      }
      
      // Wait for orgId to be available (needed for Supabase RLS filtering)
      if (!orgId) {
        return;
      }
      
      // Prevent re-running if we've already loaded documents
      if (hasLoadedDocumentsRef.current) {
        return; // Already loaded documents
      }
      
      console.log("🔄 User and orgId available, loading documents...");
      hasLoadedDocumentsRef.current = true;
      
      try {
        setIsLoading(true);
        console.log("🔍 Loading documents for user:", user.id, "orgId:", orgId);

        const response = await knowledgeService.getDocuments(undefined, undefined, orgId, user.id, undefined);
        console.log("📄 Received documents response:", response);

        // Backend returns {documents: Array, total: number}, extract documents array
        const docs = (response && typeof response === 'object' && 'documents' in response)
          ? (response as { documents: any[]; total: number }).documents
          : Array.isArray(response)
          ? response
          : [];

        // Ensure docs is always an array and has proper structure
        // Map backend format {name, category} to frontend Document format
        // Use doc.name as the ID since that's what the database uses for identification (doc_name column)
        const validDocs = docs
          .filter((doc) => doc && typeof doc === "object" && doc.name)
          .map((doc, index) => ({
            ...doc, // Include any other properties first
            id: doc.name, // Override id with document name (matches database doc_name column)
            name: doc.name,
            type: doc.type || "application/pdf",
            status: doc.status || "success" as DocumentStatus,
            uploadedAt: doc.uploadedAt || new Date().toISOString(),
            size: doc.size || 0,
            category: doc.category,
          }));

        console.log("✅ Valid documents:", validDocs.length);
        setDocuments(validDocs);
        // Load stats once after initial document fetch (not on every update)
        setTimeout(() => loadStats(), 0);
      } catch (error) {
        console.error("❌ Failed to load documents:", error);
        showError(
          "Failed to load documents",
          "Please refresh the page and try again."
        );
        // Set empty array on error
        setDocuments([]);
        // Reset ref on error so user can retry
        hasLoadedDocumentsRef.current = false;
      } finally {
        setIsLoading(false);
      }
    };
    
    loadDocumentsOnce();
  }, [user, orgId]); // Re-run when user or orgId loads (same as QuickCompare)



  const loadStats = async (): Promise<void> => {
    try {
      console.log("📊 Calculating stats from documents...");

      // Calculate stats from documents + graph stats API
      const docs = documents || [];
      let entityCount = 0;
      try {
        const graphStats = await knowledgeService.getGraphStats(orgId, user?.id);
        entityCount = graphStats?.nodeCount ?? 0;
      } catch {
        // graph stats unavailable — leave entityCount as 0
      }
      const statsData = {
        totalDocuments: docs.length,
        graphCount: docs.filter((doc: any) => doc.graph_status === 'completed').length,
        pageIndexCount: docs.filter((doc: any) => doc.page_index_status === 'completed').length,
        entityCount,
        lastUpdated: new Date().toISOString(),
      };

      console.log("📈 Calculated stats:", statsData);
      setStats(statsData);
    } catch (error) {
      console.error("❌ Failed to calculate stats:", error);
      // Set default stats on error
      setStats({
        totalDocuments: 0,
        graphCount: 0,
        pageIndexCount: 0,
        entityCount: 0,
        lastUpdated: new Date().toISOString(),
      });
    }
  };

  // Stage files on drop — do NOT upload immediately
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      // Validate files
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      acceptedFiles.forEach((file) => {
        const validation = knowledgeService.validateFile(file);
        if (validation.isValid) {
          validFiles.push(file);
        } else {
          invalidFiles.push(`${file.name}: ${validation.error}`);
        }
      });

      if (invalidFiles.length > 0) {
        showError("Some files were rejected", invalidFiles.join("\n"));
      }

      if (validFiles.length === 0) return;

      setStagedFiles(validFiles);
      setFileCustomNames(validFiles.map(f => f.name));
      setChunkingRecommendation(null);

      // Auto-trigger AI analysis for first file (chunking + category + tags + ontology)
      if (validFiles.length > 0 && uploadProviders.has('knowledge')) {
        setIsRecommending(true);
        try {
          const text = await validFiles[0].slice(0, 4000).text();
          const analysis = await knowledgeService.analyzeDocument(
            text,
            validFiles[0].name,
            documentCategory || undefined
          );
          setChunkingRecommendation(analysis.chunking);
          setChunkingStrategy(analysis.chunking.recommended);
          if (analysis.category && analysis.category !== 'general') {
            setDocumentCategory(analysis.category);
          }
          if (analysis.subcategory) {
            setDocumentSubCategory(analysis.subcategory);
          }
          if (analysis.tags.length > 0) {
            setDocumentTags(analysis.tags);
          }
          // Handle ontology based on mode
          if (analysis.ontology.entityTypes.length > 0) {
            if (ontologyMode === 'dynamic') {
              // Pure AI — replace entirely
              setRecommendedOntology(analysis.ontology);
            } else if (ontologyMode === 'hybrid' && recommendedOntology) {
              // Hybrid — merge AI additions into static base
              const existingNames = new Set(recommendedOntology.entityTypes.map(e => e.name));
              const existingRelNames = new Set(recommendedOntology.relationshipTypes.map(r => r.name));
              const newEntities = analysis.ontology.entityTypes.filter(e => !existingNames.has(e.name));
              const newRels = analysis.ontology.relationshipTypes.filter(r => !existingRelNames.has(r.name));
              if (newEntities.length > 0 || newRels.length > 0) {
                setRecommendedOntology({
                  entityTypes: [...recommendedOntology.entityTypes, ...newEntities],
                  relationshipTypes: [...recommendedOntology.relationshipTypes, ...newRels],
                });
              }
            }
            // static mode — keep preconfigured, ignore AI ontology
          }
        } catch (err) {
          console.warn('Auto-analysis failed, falling back to chunking-only:', err);
          try {
            const text = await validFiles[0].slice(0, 4000).text();
            const rec = await knowledgeService.getChunkingRecommendation(text, validFiles[0].name, documentCategory || undefined);
            setChunkingRecommendation(rec);
            setChunkingStrategy(rec.recommended);
          } catch (e) {
            console.warn('Chunking recommendation also failed:', e);
          }
        } finally {
          setIsRecommending(false);
        }
      }
    },
    [showError, documentCategory, uploadProviders]
  );

  // Actual upload of staged files — triggered by explicit button click
  const handleUploadStaged = useCallback(
    async () => {
      if (stagedFiles.length === 0) return;

      setUploading(true);
      setUploadCancelled(false);
      clearError();

      const controller = new AbortController();
      setUploadController(controller);

      try {
        if (!user?.id) {
          setUploading(false);
          setUploadController(null);
          showError("Authentication Error", "User is not authenticated. Please log in and try again.");
          return;
        }

        if (!orgId) {
          setUploading(false);
          setUploadController(null);
          showError("Organization Error", "Organization ID is not available. Please wait a moment and try again, or refresh the page.");
          return;
        }

        if (uploadProviders.size === 0) {
          setUploading(false);
          setUploadController(null);
          showError("No Provider Selected", "Please select at least one provider to upload to.");
          return;
        }

        showInfo(
          KNOWLEDGE_TOAST_MESSAGES.UPLOAD_PROCESSING,
          `Processing ${stagedFiles.length} file(s) to ${uploadProviders.size} provider(s)...`
        );

        const allUploadedDocuments: any[] = [];
        const allErrors: any[] = [];
        const providersArray = Array.from(uploadProviders);

        for (const uploadProvider of providersArray) {
          try {
            if (uploadProvider === 'gemini' && !geminiStoreId) {
              allErrors.push({
                fileName: 'All files',
                error: 'Gemini store not available. Please ensure Gemini store is created.',
              });
              continue;
            }

            showInfo(
              `Uploading to ${uploadProvider === 'gemini' ? 'Gemini FileSearch' : 'Knowledge Base'}...`,
              `Uploading ${stagedFiles.length} file(s) to ${uploadProvider === 'gemini' ? 'Gemini' : 'Knowledge Base'}`
            );

            const response = await knowledgeService.uploadDocuments(
              stagedFiles,
              (progressData) => {
                Object.entries(progressData).forEach(([documentId, progress]) => {
                  setUploadProgress({
                    documentId: `${uploadProvider}-${documentId}`,
                    progress: progress.progress,
                    status: progress.status,
                    message: `[${uploadProvider === 'gemini' ? 'Gemini' : 'KB'}] ${progress.message}`,
                  });
                });
              },
              {
                companyId: "default",
                desc: documentDescription.trim() || undefined,
                docdesc: documentDescription.trim() || undefined,
                category: documentCategory.trim() || "general",
                subCategory: documentSubCategory.trim() || undefined,
                provider: uploadProvider,
                tier: uploadProvider === 'knowledge' ? parseTier : undefined,
                language: uploadProvider === 'knowledge' ? [parseLanguage] : undefined,
                parsingInstructions: uploadProvider === 'knowledge' && parsingInstructions.trim() ? parsingInstructions.trim() : undefined,
                chunkingStrategy: uploadProvider === 'knowledge' ? chunkingStrategy : undefined,
                storeId: uploadProvider === 'gemini' ? geminiStoreId : undefined,
                orgIds: docVisibility === 'global' ? '[]' : JSON.stringify([orgId]),
                visibility: docVisibility,
                pageIndex: uploadProvider === 'knowledge' ? buildPageIndex : undefined,
                tags: documentTags.length > 0 ? documentTags : undefined,
                docNames: fileCustomNames.length > 0 ? fileCustomNames : undefined,
              },
              orgId,
              user.id
            );

            if (response.documents && Array.isArray(response.documents)) {
              allUploadedDocuments.push(...response.documents);
            }

            if (response.errors && response.errors.length > 0) {
              allErrors.push(...response.errors.map(err => ({
                ...err,
                provider: uploadProvider,
              })));
            }
          } catch (error) {
            console.error(`Upload to ${uploadProvider} failed:`, error);
            allErrors.push({
              fileName: 'All files',
              error: `Failed to upload to ${uploadProvider === 'gemini' ? 'Gemini' : 'Knowledge Base'}: ${error instanceof Error ? error.message : 'Unknown error'}`,
              provider: uploadProvider,
            });
          }
        }

        if (allUploadedDocuments.length > 0) {
          console.log("Processing uploaded documents:", allUploadedDocuments);

          allUploadedDocuments.forEach((doc) => {
            if (doc && doc.id) {
              clearUploadProgress(doc.id);
            }
          });

          showSuccess(
            KNOWLEDGE_TOAST_MESSAGES.UPLOAD_SUCCESS,
            `Successfully uploaded ${allUploadedDocuments.length} document(s) to ${providersArray.map(p => p === 'gemini' ? 'Gemini' : 'KB').join(' and ')}`
          );

          // Clear form and staged files after successful upload
          setStagedFiles([]);
          setFileCustomNames([]);
          setDocumentDescription("");
          setDocumentCategory("");
          setDocumentSubCategory("");
          setParsingInstructions("");
          setChunkingRecommendation(null);

          // Reload the full document list from the backend so visibility,
          // graph_status, and page_index_status are accurate.
          await loadDocumentsManually();

          if (allErrors.length === 0) {
            scheduleAutoClose();
          }
        } else {
          console.warn("No documents uploaded to any provider");
        }

        if (allErrors.length > 0) {
          const errorMessages = allErrors.map(
            (err) => `[${err.provider || 'Unknown'}] ${err.fileName}: ${err.error}`
          );
          showError("Some uploads failed", errorMessages.join("\n"));
        }

        setTimeout(() => loadStats(), 100);
      } catch (error) {
        console.error("Upload failed:", error);

        if (error instanceof Error && error.name === 'AbortError') {
          console.log("Upload was cancelled by user");
          return;
        }

        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        showError(
          KNOWLEDGE_TOAST_MESSAGES.UPLOAD_ERROR,
          errorMessage
        );
      } finally {
        setUploading(false);
        setUploadController(null);
      }
    },
    [
      stagedFiles,
      documentDescription,
      documentCategory,
      documentSubCategory,
      parseTier,
      parseLanguage,
      parsingInstructions,
      chunkingStrategy,
      docVisibility,
      uploadProviders,
      geminiStoreId,
      orgId,
      user,
      setUploading,
      clearError,
      setUploadProgress,
      clearUploadProgress,
      addDocument,
      setStats,
      showSuccess,
      showError,
      showInfo,
      setUploadController,
      setUploadCancelled,
      scheduleAutoClose
    ]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      // PDF files
      "application/pdf": [".pdf"],

      // Word documents
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],

      // Text files
      "text/plain": [".txt"],

      // Markdown files (multiple MIME types for compatibility)
      "text/markdown": [".md"],
      "text/x-markdown": [".md"],
      "application/x-markdown": [".md"],

      // RTF files
      "application/rtf": [".rtf"],

      // OpenDocument Text
      "application/vnd.oasis.opendocument.text": [".odt"],
    },
    multiple: true,
    disabled: isUploading,
    // Allow files to be accepted even if MIME type doesn't match exactly
    validator: (file) => {
      const validExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md', '.rtf', '.odt'];
      const fileExtension = '.' + file.name.toLowerCase().split('.').pop();

      if (validExtensions.includes(fileExtension)) {
        return null; // Valid file
      }

      return {
        code: "file-invalid-type",
        message: `File type not supported. Supported formats: PDF, Word (.doc/.docx), Text (.txt), Markdown (.md), RTF, ODT.`
      };
    }
  });

  const handleDeleteDocument = async (documentId: string): Promise<void> => {
    try {
      // Show confirmation dialog
      if (
        !window.confirm(
          "Are you sure you want to delete this document? This action cannot be undone."
        )
      ) {
        return;
      }

      console.log(`🗑️ Deleting document: ${documentId}`);

      // Validate user and orgId before deleting
      if (!user?.id) {
        throw new Error("User is not authenticated. Please log in.");
      }
      if (!orgId) {
        throw new Error("Organization ID is required for delete.");
      }

      // Call the backend to delete from Knowledge Base
      await knowledgeService.deleteDocument(documentId, orgId, user.id);

      // Remove from local state
      removeDocument(documentId);
      setSelectedDocuments((prev) => prev.filter((id) => id !== documentId));

      showSuccess(
        "Document deleted successfully",
        "The document has been removed from your knowledge base."
      );

      // Reload documents to ensure consistency
      setTimeout(() => {
        loadDocumentsManually();
        loadStats();
      }, 500);
    } catch (error) {
      console.error("Failed to delete document:", error);
      showError(
        "Failed to delete document",
        error instanceof Error
          ? error.message
          : "Unknown error occurred while deleting the document."
      );
    }
  };

  const [reprocessingDoc, setReprocessingDoc] = useState<string | null>(null);
  const [retryingPageIndex, setRetryingPageIndex] = useState<string | null>(null);
  const [editingDoc, setEditingDoc] = useState<{ name: string; newName: string; category: string; tags: string[]; tagInput: string; visibility: string } | null>(null);
  const [isSavingDoc, setIsSavingDoc] = useState(false);
  const [showReprocessDialog, setShowReprocessDialog] = useState<string | null>(null);
  const [reprocessOntologyMode, setReprocessOntologyMode] = useState<'static' | 'hybrid' | 'dynamic'>('hybrid');
  const [reprocessForceOntology, setReprocessForceOntology] = useState(false);
  const [reprocessCategory, setReprocessCategory] = useState('');

  const handleReprocessGraph = async (docName: string): Promise<void> => {
    if (!graphEnabled) {
      showInfo("Graph not enabled", "Enable graph features in .env to use reprocessing.");
      return;
    }
    // Open dialog instead of immediate reprocess
    setShowReprocessDialog(docName);
    setReprocessCategory('');
    setReprocessForceOntology(false);
  };

  const executeReprocess = async (): Promise<void> => {
    const docName = showReprocessDialog;
    if (!docName) return;

    setShowReprocessDialog(null);
    setReprocessingDoc(docName);
    showInfo("Reprocessing", `Re-extracting entities for "${docName}"...`);

    try {
      const result = await knowledgeService.reprocessGraph(
        docName,
        reprocessForceOntology,
        reprocessCategory || undefined
      );

      if (result.success) {
        showSuccess(
          "Graph reprocessed",
          `Extracted ${result.entities} entities and ${result.relationships} relationships.`
        );
      } else {
        showError("Reprocess failed", result.error || "Unknown error");
      }
    } catch (error) {
      console.error("Failed to reprocess graph:", error);
      showError(
        "Failed to reprocess graph",
        error instanceof Error ? error.message : "Unknown error"
      );
    } finally {
      setReprocessingDoc(null);
    }
  };

  const handleRetryPageIndex = async (docName: string): Promise<void> => {
    setRetryingPageIndex(docName);
    showInfo("Rebuilding page index", `Page index rebuild started for "${docName}"...`);
    try {
      await knowledgeService.retryPageIndex(docName, orgId, user?.id);
      // Reload after a short delay to pick up the new processing status
      setTimeout(() => loadDocumentsManually(), 1500);
    } catch (error) {
      showError("Retry failed", error instanceof Error ? error.message : "Unknown error");
    } finally {
      setRetryingPageIndex(null);
    }
  };

  const handleSaveDocEdit = async (): Promise<void> => {
    if (!editingDoc || !user || !orgId) return;
    setIsSavingDoc(true);
    try {
      const result = await knowledgeService.updateDocument(
        editingDoc.name,
        { category: editingDoc.category, tags: editingDoc.tags, name: editingDoc.newName, visibility: editingDoc.visibility as 'private' | 'org' | 'global' },
        orgId,
        user.id
      );
      if (result.success) {
        const displayName = editingDoc.newName !== editingDoc.name ? editingDoc.newName : editingDoc.name;
        showSuccess('Document updated', `Saved "${displayName}"`);
        setEditingDoc(null);
        setTimeout(() => loadDocumentsManually(), 500);
      } else {
        showError('Update failed', result.error || 'Unknown error');
      }
    } catch (error) {
      showError('Update failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsSavingDoc(false);
    }
  };

  // Combine Knowledge Base documents and Gemini files
  const allDocuments = useMemo(() => {
    const kbDocs = (documents || []).map(doc => ({
      ...doc,
      source: 'knowledge' as const,
    }));
    
    // Add Gemini files when provider is Gemini
    const geminiDocs = provider === 'gemini' && geminiFiles.length > 0
      ? geminiFiles.map(file => ({
          id: file.name,
          name: file.name,
          type: "application/pdf",
          status: "success" as DocumentStatus,
          uploadedAt: new Date().toISOString(),
          size: 0,
          category: file.category,
          source: 'gemini' as const,
        }))
      : [];
    
    return [...kbDocs, ...geminiDocs];
  }, [documents, provider, geminiFiles]);

  const filteredDocuments = allDocuments.filter((doc) => {
    if (!doc || typeof doc !== "object") return false;
    if (!doc.name || typeof doc.name !== "string") return false;

    const matchesSearch = doc.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || doc.status === statusFilter;
    const matchesVisibility = visibilityFilter === "all" || (doc as any).visibility === visibilityFilter;
    const d = doc as any;
    const matchesPipeline =
      pipelineFilter === 'all' ||
      (pipelineFilter === 'graph_processing' && d.graph_status === 'processing') ||
      (pipelineFilter === 'graph_failed'     && d.graph_status === 'failed') ||
      (pipelineFilter === 'index_processing' && d.page_index_status === 'processing') ||
      (pipelineFilter === 'index_completed'  && d.page_index_status === 'completed') ||
      (pipelineFilter === 'index_failed'     && d.page_index_status === 'failed');
    return matchesSearch && matchesStatus && matchesVisibility && matchesPipeline;
  });

  // Debug logging
  useEffect(() => {
    console.log("🔍 Documents state:", {
      documentsLength: documents?.length || 0,
      filteredLength: filteredDocuments.length,
      searchQuery,
      statusFilter,
      sampleDoc: documents?.[0],
    });
  }, [documents, filteredDocuments.length, searchQuery, statusFilter]);

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case "success":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case "processing":
      case "uploading":
      case "parsing":
      case "indexing":
        return <Clock className="w-4 h-4 text-yellow-500 animate-pulse" />;
      default:
        return <FileText className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStatusText = (status: DocumentStatus) => {
    switch (status) {
      case "uploading":
        return "Uploading...";
      case "parsing":
        return "Parsing...";
      case "indexing":
        return "Indexing...";
      case "processing":
        return "Processing...";
      case "success":
        return "Ready";
      case "error":
        return "Error";
      default:
        return status;
    }
  };

  const getVisibilityBadge = (visibility?: string) => {
    switch (visibility) {
      case 'global':
        return (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
            <Globe className="w-3 h-3" /> Global
          </span>
        );
      case 'org':
        return (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
            <Building2 className="w-3 h-3" /> Org
          </span>
        );
      case 'private':
      default:
        return (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
            <Lock className="w-3 h-3" /> Private
          </span>
        );
    }
  };

  // Chat functionality
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: inputMessage,
      timestamp: new Date().toISOString(),
    };

    setChatMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsSending(true);
    setChatError(null);

    try {
      // Validate user and orgId before chat
      if (!user?.id) {
        throw new Error("User is not authenticated. Please log in.");
      }
      if (!orgId) {
        throw new Error("Organization ID is required for chat.");
      }

      const response = await knowledgeService.chatWithRAG({
        message: inputMessage,
        selectedDocuments: selectedDocuments,
        systemMessage: DEFAULT_SYSTEM_MESSAGE,
        temperature: chatTemperature,
        maxTokens: chatMaxTokens,
        searchMode: searchMode,
        topK: chatTopK,
        minScore: chatMinScore,
      }, orgId, user.id, provider, provider === 'gemini' ? geminiStoreId || undefined : undefined);

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: response.message || "I'm here to help you with your documents!",
        timestamp: new Date().toISOString(),
        sources: response.sources || [],
        metadata: response.metadata,
        retrieval: response.retrieval as ChatMessage['retrieval'],
        goldenMatch: response.goldenMatch,
        graphContext: response.graphContext,
        ragChunks: response.ragChunks,
        pageIndexSections: response.pageIndexSections,
      };

      setChatMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      setChatError("Failed to get response. Please try again.");

      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date().toISOString(),
      };
      setChatMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleDocumentSelection = (documentId: string) => {
    setSelectedDocuments((prev) =>
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId]
    );
  };

  const clearChat = () => {
    setChatMessages([]);
    setChatError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header - CRM Style */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Knowledge Base
            </h1>
            <p className="text-gray-600 text-lg">
              Upload documents and chat with your knowledge base
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadDocumentsManually}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 hover:border-gray-400 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw
                className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
              />
              {isLoading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>


        {/* Tab Navigation - CRM Style */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("upload")}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "upload"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4" />
                <span>Documents</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab("chat")}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "chat"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center space-x-2">
                <MessageSquare className="w-4 h-4" />
                <span>Chat with Data</span>
              </div>
            </button>
            {graphEnabled && (
              <button
                onClick={() => setActiveTab("graph")}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "graph"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Brain className="w-4 h-4" />
                  <span>Graph Explorer</span>
                </div>
              </button>
            )}
            <button
              onClick={() => setActiveTab("settings")}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "settings"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center space-x-2">
                <Database className="w-4 h-4" />
                <span>Settings</span>
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "upload" && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              {/* Stats Cards - CRM Style */}
              {stats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Total Documents
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {stats.totalDocuments}
                        </p>
                      </div>
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Database className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Graphs Built
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {stats.graphCount}
                        </p>
                      </div>
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Brain className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Page Indexed
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {stats.pageIndexCount}
                        </p>
                      </div>
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Entities
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {stats.entityCount}
                        </p>
                      </div>
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Network className="w-6 h-6 text-orange-600" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Documents List - Enhanced for 100+ documents */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        Your Documents
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {filteredDocuments.length} of {documents?.length || 0}{" "}
                        documents
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                      {/* Refresh Button */}
                      <button
                        onClick={loadDocumentsManually}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 hover:border-gray-400 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white"
                        title="Refresh documents list"
                      >
                        <RefreshCw
                          className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
                        />
                        {isLoading ? "Loading..." : "Refresh"}
                      </button>
                      
                      {/* Fetch Gemini Files Button */}
                      {provider === 'gemini' && geminiStoreId && (
                        <button
                          onClick={fetchGeminiFiles}
                          disabled={isLoadingGeminiFiles}
                          className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 hover:border-gray-400 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white"
                          title="Fetch files from Gemini store"
                        >
                          <RefreshCw
                            className={`w-4 h-4 ${isLoadingGeminiFiles ? "animate-spin" : ""}`}
                          />
                          {isLoadingGeminiFiles ? "Fetching..." : "Fetch Gemini Files"}
                        </button>
                      )}
                      
                      {/* Upload Button - Matching New Proposal button style */}
                      <button
                        onClick={() => setShowUploadDialog(true)}
                        disabled={isUploading || isOrgIdLoading}
                        title={isOrgIdLoading ? "Loading workspace..." : undefined}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                      >
                        <Upload className="w-4 h-4" />
                        {isUploading ? "Uploading..." : isOrgIdLoading ? "Loading..." : "Upload"}
                      </button>

                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search documents..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-52 pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>

                      <select
                        value={visibilityFilter}
                        onChange={(e) => setVisibilityFilter(e.target.value as DocumentVisibility | "all")}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">All Visibility</option>
                        <option value="private">Private</option>
                        <option value="org">Organisation</option>
                        <option value="global">Global</option>
                      </select>

                      <select
                        value={pipelineFilter}
                        onChange={(e) => setPipelineFilter(e.target.value as typeof pipelineFilter)}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">All Pipelines</option>
                        <option value="graph_processing">Graph: Processing</option>
                        <option value="graph_failed">Graph: Failed</option>
                        <option value="index_processing">Index: Processing</option>
                        <option value="index_completed">Index: Ready</option>
                        <option value="index_failed">Index: Failed</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Documents Table */}
                <div className="overflow-x-auto">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
                      <span className="ml-3 text-sm text-gray-600">Loading documents…</span>
                    </div>
                  ) : filteredDocuments.length === 0 ? (
                    <div className="text-center py-16">
                      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-500">No documents found</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {searchQuery || pipelineFilter !== 'all' || visibilityFilter !== 'all'
                          ? "Try adjusting your filters."
                          : "Upload your first document to get started."}
                      </p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-8">#</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Document / Category / Tags</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">Visibility</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">Graph</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">Page Index</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">Uploaded</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-32">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredDocuments.map((doc, index) => {
                          const d = doc as any;
                          const isSelected = selectedDocuments.includes(doc.id);
                          return (
                            <React.Fragment key={doc.id}>
                            <tr
                              className={`group hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                            >
                              {/* # */}
                              <td className="px-4 py-3 text-xs text-gray-400">{index + 1}</td>

                              {/* Document name + category + tags */}
                              <td className="px-4 py-3 max-w-xs">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="truncate font-medium text-gray-900 text-sm" title={doc.name}>{doc.name}</span>
                                  {d.source === 'gemini' && (
                                    <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Gemini</span>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-1 mt-1">
                                  {doc.category && (
                                    <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 font-medium">
                                      {doc.category}
                                    </span>
                                  )}
                                  {d.subcategory && (
                                    <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100">
                                      {d.subcategory}
                                    </span>
                                  )}
                                  {(Array.isArray(d.tags) ? d.tags : (typeof d.tags === 'string' ? d.tags.split(',') : [])).filter(Boolean).map((tag: string) => (
                                    <span key={tag} className="inline-flex items-center text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                                      {tag.trim()}
                                    </span>
                                  ))}
                                </div>
                              </td>

                              {/* Visibility */}
                              <td className="px-4 py-3">
                                {getVisibilityBadge(d.visibility)}
                              </td>

                              {/* Graph status */}
                              <td className="px-4 py-3">
                                {d.graph_status === 'processing' && (
                                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200">
                                    <RefreshCw className="w-3 h-3 animate-spin" />Running
                                  </span>
                                )}
                                {d.graph_status === 'completed' && (
                                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />Ready
                                  </span>
                                )}
                                {d.graph_status === 'failed' && (
                                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200" title={d.graph_error || ''}>
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />Failed
                                  </span>
                                )}
                                {!d.graph_status && (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>

                              {/* Page index status */}
                              <td className="px-4 py-3">
                                {d.page_index_status === 'processing' && (
                                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200">
                                    <RefreshCw className="w-3 h-3 animate-spin" />Building
                                  </span>
                                )}
                                {d.page_index_status === 'completed' && (
                                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />Indexed
                                  </span>
                                )}
                                {d.page_index_status === 'failed' && (
                                  <span className="text-xs text-gray-400" title={d.page_index_error || 'Page index not available for this document'}>N.A</span>
                                )}
                                {!d.page_index_status && (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>

                              {/* Uploaded date */}
                              <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                                {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : '—'}
                              </td>

                              {/* Actions */}
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end gap-1">
                                  {/* Select for chat */}
                                  <button
                                    onClick={() => toggleDocumentSelection(doc.id)}
                                    className={`px-2 py-1 text-xs rounded transition-colors ${
                                      isSelected
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                                    }`}
                                    title={isSelected ? 'Deselect' : 'Select for chat'}
                                  >
                                    {isSelected ? 'Selected' : 'Select'}
                                  </button>

                                  {/* Edit */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (editingDoc?.name === doc.id) {
                                        setEditingDoc(null);
                                      } else {
                                        setEditingDoc({
                                          name: doc.id,
                                          newName: doc.id,
                                          category: d.category || '',
                                          tags: Array.isArray(d.tags) ? d.tags : (typeof d.tags === 'string' ? d.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : []),
                                          tagInput: '',
                                          visibility: d.visibility || 'private',
                                        });
                                      }
                                    }}
                                    className={`p-1.5 rounded transition-colors ${editingDoc?.name === doc.id ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                                    title="Edit category & tags"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>

                                  {/* Reprocess graph */}
                                  {graphEnabled && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleReprocessGraph(doc.id); }}
                                      disabled={reprocessingDoc === doc.id}
                                      className="p-1.5 text-purple-500 hover:bg-purple-50 rounded transition-colors disabled:opacity-50"
                                      title="Reprocess knowledge graph"
                                    >
                                      {reprocessingDoc === doc.id
                                        ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                        : <Brain className="w-3.5 h-3.5" />}
                                    </button>
                                  )}

                                  {/* View entities in graph */}
                                  {graphEnabled && d.graph_status === 'completed' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setGraphDocFilter(doc.id);
                                        setActiveTab('graph');
                                      }}
                                      className="p-1.5 text-indigo-500 hover:bg-indigo-50 rounded transition-colors"
                                      title="View entities in graph"
                                    >
                                      <Network className="w-3.5 h-3.5" />
                                    </button>
                                  )}

                                  {/* Delete */}
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteDocument(doc.id); }}
                                    className="p-1.5 text-red-400 hover:bg-red-50 rounded transition-colors"
                                    title="Delete document"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>

                            {/* Inline edit row */}
                            {editingDoc?.name === doc.id && (
                              <tr className="bg-blue-50 border-b border-blue-100">
                                <td colSpan={7} className="px-4 py-4">
                                  <div className="flex flex-col gap-3">
                                    <div className="flex items-center gap-4 flex-wrap">

                                      {/* Name */}
                                      <div className="flex flex-col gap-1 min-w-[220px]">
                                        <label className="text-xs font-medium text-gray-600">Document Name</label>
                                        <input
                                          type="text"
                                          value={editingDoc.newName}
                                          onChange={(e) => setEditingDoc({ ...editingDoc, newName: e.target.value })}
                                          className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                                          placeholder="Document name"
                                        />
                                      </div>

                                      {/* Category */}
                                      <div className="flex flex-col gap-1 min-w-[180px]">
                                        <label className="text-xs font-medium text-gray-600">Category</label>
                                        <select
                                          value={editingDoc.category}
                                          onChange={(e) => setEditingDoc({ ...editingDoc, category: e.target.value })}
                                          className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                                        >
                                          <option value="">— select —</option>
                                          {loadedCategories.map((c) => (
                                            <option key={c.name} value={c.name}>{c.name}</option>
                                          ))}
                                          {editingDoc.category && !loadedCategories.find(c => c.name === editingDoc.category) && (
                                            <option value={editingDoc.category}>{editingDoc.category} (current)</option>
                                          )}
                                        </select>
                                      </div>

                                      {/* Tags */}
                                      <div className="flex flex-col gap-1 flex-1 min-w-[240px]">
                                        <label className="text-xs font-medium text-gray-600">Tags</label>
                                        <div className="flex flex-wrap gap-1 p-1.5 border border-gray-300 rounded bg-white min-h-[34px]">
                                          {editingDoc.tags.map((tag) => (
                                            <span key={tag} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                              {tag}
                                              <button onClick={() => setEditingDoc({ ...editingDoc, tags: editingDoc.tags.filter(t => t !== tag) })} className="hover:text-red-500">
                                                <X className="w-3 h-3" />
                                              </button>
                                            </span>
                                          ))}
                                          <input
                                            type="text"
                                            value={editingDoc.tagInput}
                                            onChange={(e) => setEditingDoc({ ...editingDoc, tagInput: e.target.value })}
                                            onKeyDown={(e) => {
                                              if ((e.key === 'Enter' || e.key === ',') && editingDoc.tagInput.trim()) {
                                                e.preventDefault();
                                                const newTag = editingDoc.tagInput.trim().toLowerCase();
                                                if (!editingDoc.tags.includes(newTag)) {
                                                  setEditingDoc({ ...editingDoc, tags: [...editingDoc.tags, newTag], tagInput: '' });
                                                } else {
                                                  setEditingDoc({ ...editingDoc, tagInput: '' });
                                                }
                                              }
                                            }}
                                            placeholder="Add tag, press Enter"
                                            className="text-xs outline-none flex-1 min-w-[100px] bg-transparent"
                                          />
                                        </div>
                                      </div>
                                    </div>

                                    {/* Visibility */}
                                    <div className="flex flex-col gap-1 min-w-[160px]">
                                      <label className="text-xs font-medium text-gray-600">Visibility / Scope</label>
                                      <select
                                        value={editingDoc.visibility}
                                        onChange={(e) => setEditingDoc({ ...editingDoc, visibility: e.target.value })}
                                        className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                                      >
                                        <option value="private">Private (only me)</option>
                                        <option value="org">Org (my organisation)</option>
                                        <option value="global">Global (all users)</option>
                                      </select>
                                    </div>

                                    {/* Actions row */}
                                    <div className="flex items-center gap-2 flex-wrap">
                                      {/* Save */}
                                      <button
                                        onClick={handleSaveDocEdit}
                                        disabled={isSavingDoc}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                      >
                                        <Save className="w-3.5 h-3.5" />
                                        {isSavingDoc ? 'Saving…' : 'Save'}
                                      </button>

                                      {/* Build Graph */}
                                      {graphEnabled && (
                                        <button
                                          onClick={() => { handleReprocessGraph(doc.id); setEditingDoc(null); }}
                                          disabled={reprocessingDoc === doc.id}
                                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-purple-50 text-purple-700 border border-purple-200 rounded hover:bg-purple-100 disabled:opacity-50 transition-colors"
                                        >
                                          <Brain className="w-3.5 h-3.5" />
                                          Build Graph
                                        </button>
                                      )}

                                      {/* Reset stuck graph */}
                                      {d.graph_status === 'processing' && (
                                        <button
                                          onClick={async () => {
                                            await knowledgeService.updateDocument(doc.id, { resetGraphStatus: true }, orgId, user?.id);
                                            setEditingDoc(null);
                                            setTimeout(() => loadDocumentsManually(), 500);
                                          }}
                                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 rounded hover:bg-yellow-100 transition-colors"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                          Reset Graph
                                        </button>
                                      )}

                                      {/* Build Page Index */}
                                      <button
                                        onClick={() => { handleRetryPageIndex(doc.id); setEditingDoc(null); }}
                                        disabled={retryingPageIndex === doc.id}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded hover:bg-indigo-100 disabled:opacity-50 transition-colors"
                                      >
                                        <RefreshCw className={`w-3.5 h-3.5 ${retryingPageIndex === doc.id ? 'animate-spin' : ''}`} />
                                        Build Page Index
                                      </button>

                                      {/* Reset stuck page index */}
                                      {d.page_index_status === 'processing' && (
                                        <button
                                          onClick={async () => {
                                            await knowledgeService.updateDocument(doc.id, { resetPageIndexStatus: true }, orgId, user?.id);
                                            setEditingDoc(null);
                                            setTimeout(() => loadDocumentsManually(), 500);
                                          }}
                                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 rounded hover:bg-yellow-100 transition-colors"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                          Reset Index
                                        </button>
                                      )}

                                      {/* Cancel */}
                                      <button
                                        onClick={() => setEditingDoc(null)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Bulk Actions - CRM Style */}
                {filteredDocuments.length > 0 && (
                  <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">
                          {filteredDocuments.length}
                        </span>{" "}
                        of{" "}
                        <span className="font-medium">
                          {documents?.length || 0}
                        </span>{" "}
                        documents
                        {selectedDocuments.length > 0 && (
                          <span className="ml-2 text-blue-600">
                            • {selectedDocuments.length} selected
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => {
                            const readyDocs = filteredDocuments.filter(
                              (doc) => doc.status === "success"
                            );
                            setSelectedDocuments(
                              readyDocs.map((doc) => doc.id)
                            );
                          }}
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={
                            filteredDocuments.filter(
                              (doc) => doc.status === "success"
                            ).length === 0
                          }
                        >
                          Select All Ready
                        </button>
                        <button
                          onClick={() => setSelectedDocuments([])}
                          className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={selectedDocuments.length === 0}
                        >
                          Clear Selection
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === "chat" && (
            <motion.div
              key="chat"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Chat Container with fixed height to prevent footer override */}
              <div className="h-[600px] w-full">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-full">
                  {/* Document Selection Sidebar */}
                  <div className="lg:col-span-1">
                    <div className="dashboard-card p-4 h-full flex flex-col">
                      <h3 className="font-semibold mb-4 flex items-center space-x-2">
                        <FileText className="w-5 h-5" />
                        <span>Select Documents</span>
                      </h3>

                      {/* Search */}
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="Search documents..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-sm"
                        />
                      </div>

                      {/* Documents List */}
                      <div className="flex-1 overflow-y-auto space-y-2 mb-4 max-h-[350px]">
                        {isLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="loading-spinner w-5 h-5"></div>
                            <span className="ml-2 text-sm text-muted-foreground">
                              Loading...
                            </span>
                          </div>
                        ) : filteredDocuments.length === 0 ? (
                          <div className="text-center py-8">
                            <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">
                              No documents found
                            </p>
                            <button
                              onClick={() => setActiveTab("upload")}
                              className="text-xs text-primary hover:underline mt-2"
                            >
                              Upload documents first
                            </button>
                          </div>
                        ) : (
                          filteredDocuments.map((doc) => (
                            <div
                              key={doc.id}
                              className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${
                                selectedDocuments.includes(doc.id)
                                  ? "border-primary bg-primary/5"
                                  : "border-border hover:border-primary/50"
                              }`}
                              onClick={() => toggleDocumentSelection(doc.id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 flex-1 min-w-0">
                                  <div className="flex-shrink-0">
                                    {getStatusIcon(doc.status)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium truncate">
                                      {doc.name}
                                    </h4>
                                    <p className="text-xs text-muted-foreground">
                                      {getStatusText(doc.status)}
                                    </p>
                                  </div>
                                </div>
                                {selectedDocuments.includes(doc.id) && (
                                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Selected Count */}
                      {selectedDocuments.length > 0 && (
                        <div className="p-2 bg-primary/10 rounded-lg mb-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-primary font-medium">
                              {selectedDocuments.length} selected
                            </span>
                            <button
                              onClick={() => setSelectedDocuments([])}
                              className="text-xs text-primary hover:underline"
                            >
                              Clear all
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Quick Actions */}
                      <div className="border-t border-border pt-4">
                        <h4 className="text-sm font-medium mb-2">
                          Quick Actions
                        </h4>
                        <div className="space-y-2">
                          <button
                            onClick={() =>
                              setSelectedDocuments(
                                filteredDocuments.map((doc) => doc.id)
                              )
                            }
                            className="w-full text-xs text-left px-2 py-1 text-primary hover:bg-primary/10 rounded"
                            disabled={filteredDocuments.length === 0}
                          >
                            Select All Documents
                          </button>
                          <button
                            onClick={() => {
                              const readyDocs = filteredDocuments.filter(
                                (doc) => doc.status === "success"
                              );
                              setSelectedDocuments(
                                readyDocs.map((doc) => doc.id)
                              );
                            }}
                            className="w-full text-xs text-left px-2 py-1 text-primary hover:bg-primary/10 rounded"
                          >
                            Select Ready Documents
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Chat Interface */}
                  <div className="lg:col-span-3">
                    <div className="dashboard-card h-full flex flex-col">
                      {/* Chat Header */}
                      <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Brain className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">AI Assistant</h3>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                provider === 'gemini'
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {provider === 'gemini' ? 'Gemini FileSearch' : 'Knowledge Base (RAG)'}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {selectedDocuments.length > 0
                                ? `Chatting with ${selectedDocuments.length} document(s)`
                                : "Select documents to start chatting"}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={clearChat}
                          className="text-sm text-muted-foreground hover:text-foreground px-3 py-1 rounded-lg hover:bg-muted/50"
                        >
                          Clear chat
                        </button>
                      </div>

                      {/* Chat Messages - Fixed height for footer space */}
                      <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[400px]">
                        {chatMessages.length === 0 ? (
                          <div className="text-center py-8">
                            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-semibold mb-2">
                              Start a conversation
                            </h3>
                            <p className="text-muted-foreground mb-4">
                              Select documents from the sidebar and ask
                              questions about them
                            </p>

                            {/* Suggested Questions */}
                            {selectedDocuments.length > 0 && (
                              <div className="mt-6 max-w-md mx-auto">
                                <p className="text-sm font-medium mb-3">
                                  Try asking:
                                </p>
                                <div className="space-y-2">
                                  {[
                                    "What are the key points in these documents?",
                                    "Can you summarize the main topics?",
                                    "What are the important dates mentioned?",
                                    "Are there any action items or recommendations?",
                                  ].map((question, index) => (
                                    <button
                                      key={index}
                                      onClick={() => setInputMessage(question)}
                                      className="block w-full text-left p-2 text-sm bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                                    >
                                      "{question}"
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          chatMessages.map((message) => (
                            <div
                              key={message.id}
                              className={`flex ${
                                message.role === "user"
                                  ? "justify-end"
                                  : "justify-start"
                              }`}
                            >
                              <div
                                className={`max-w-[85%] rounded-lg p-3 ${
                                  message.role === "user"
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted"
                                }`}
                              >
                                <div className="flex items-start space-x-2">
                                  {message.role === "assistant" && (
                                    <Bot className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                                  )}
                                  <div className="flex-1">
                                    {message.role === "assistant" && message.retrieval === 'golden' && (
                                      <div className="flex items-center gap-1.5 mb-2 px-2.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-md w-fit">
                                        <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                        <span className="text-xs font-medium text-emerald-700">Verified Answer</span>
                                        {message.goldenMatch && <span className="text-xs text-emerald-500">({(message.goldenMatch.similarity * 100).toFixed(0)}% match)</span>}
                                      </div>
                                    )}
                                    {message.role === "assistant" ? (
                                      <div className="text-sm leading-relaxed kb-markdown">
                                        <ReactMarkdown
                                          components={{
                                            h1: ({ children }) => <h1 className="text-base font-bold text-gray-800 mt-3 mb-1">{children}</h1>,
                                            h2: ({ children }) => <h2 className="text-sm font-bold text-gray-800 mt-3 mb-1">{children}</h2>,
                                            h3: ({ children }) => <h3 className="text-sm font-semibold text-gray-700 mt-2 mb-1">{children}</h3>,
                                            p: ({ children }) => <p className="text-sm text-gray-700 mb-2">{children}</p>,
                                            ul: ({ children }) => <ul className="list-disc list-inside text-sm text-gray-700 mb-2 space-y-0.5">{children}</ul>,
                                            ol: ({ children }) => <ol className="list-decimal list-inside text-sm text-gray-700 mb-2 space-y-0.5">{children}</ol>,
                                            li: ({ children }) => <li className="text-sm text-gray-700">{children}</li>,
                                            strong: ({ children }) => <strong className="font-semibold text-gray-800">{children}</strong>,
                                            em: ({ children }) => <em className="italic text-gray-600">{children}</em>,
                                            code: ({ children }) => <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                                            pre: ({ children }) => <pre className="bg-gray-100 rounded-lg p-3 overflow-x-auto text-xs mb-2">{children}</pre>,
                                            blockquote: ({ children }) => <blockquote className="border-l-3 border-gray-300 pl-3 italic text-gray-600 mb-2">{children}</blockquote>,
                                            a: ({ href, children }) => <a href={href} className="text-blue-600 underline hover:text-blue-800" target="_blank" rel="noopener noreferrer">{children}</a>,
                                            table: ({ children }) => <table className="text-xs border-collapse border border-gray-200 mb-2 w-full">{children}</table>,
                                            th: ({ children }) => <th className="border border-gray-200 px-2 py-1 bg-gray-50 font-medium text-left">{children}</th>,
                                            td: ({ children }) => <td className="border border-gray-200 px-2 py-1">{children}</td>,
                                          }}
                                        >
                                          {message.content}
                                        </ReactMarkdown>
                                      </div>
                                    ) : (
                                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                        {message.content}
                                      </p>
                                    )}
                                    {message.sources &&
                                      message.sources.length > 0 && (
                                        <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
                                          <p className="text-xs font-medium text-muted-foreground mb-2">
                                            📚 Sources ({message.sources.length}):
                                          </p>
                                          <div className="space-y-1 max-h-32 overflow-y-auto">
                                            {message.sources.map((source, idx) => (
                                              <div
                                                key={idx}
                                                className="text-xs bg-muted/50 rounded px-2 py-1 border border-border/30"
                                              >
                                                <div className="flex items-start justify-between gap-2">
                                                  <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-foreground truncate">
                                                      {source.documentName}
                                                    </p>
                                                    {source.pageNumber && (
                                                      <p className="text-muted-foreground">
                                                        Page {source.pageNumber}
                                                      </p>
                                                    )}
                                                    {source.excerpt && (
                                                      <p className="text-muted-foreground mt-1 line-clamp-2">
                                                        {source.excerpt}
                                                      </p>
                                                    )}
                                                  </div>
                                                  <div className="flex-shrink-0">
                                                    <span className="text-xs text-muted-foreground">
                                                      {Math.round(source.relevanceScore * 100)}%
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    {/* Retrieval details */}
                                    {(() => {
                                      if (message.role !== 'assistant') return null;
                                      const r = message.retrieval;
                                      const isStructural = r === 'page-index' || r === 'deep' || r === 'graph';
                                      const hasGraph = !!message.graphContext;
                                      if (!isStructural && !hasGraph) return null;

                                      const modeLabel: Record<string, string> = {
                                        'page-index': 'Page Index',
                                        'deep': 'Deep',
                                        'graph': 'Graph',
                                      };

                                      // For deep mode with breakdown counts
                                      const ragChunks = message.ragChunks;
                                      const piSections = message.pageIndexSections;
                                      // Fallback: total sources when breakdown not yet available
                                      const totalSources = message.sources?.length ?? 0;

                                      return (
                                        <div className="mt-2 pt-2 border-t border-border/30">
                                          <div className="flex flex-wrap gap-1.5">
                                            {/* Mode badge */}
                                            {r && modeLabel[r] && (
                                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">
                                                <Network className="w-3 h-3" />
                                                {modeLabel[r]}
                                              </span>
                                            )}
                                            {/* RAG chunks (deep mode, post-server-restart) */}
                                            {r === 'deep' && ragChunks != null && ragChunks > 0 && (
                                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200" title="Vector chunks retrieved">
                                                <Database className="w-3 h-3" />
                                                {ragChunks} {ragChunks === 1 ? 'chunk' : 'chunks'}
                                              </span>
                                            )}
                                            {/* Page-index sections */}
                                            {(r === 'page-index' || r === 'deep') && (() => {
                                              // Use server-provided count if available, otherwise fall back to sources length
                                              const count = piSections != null ? piSections : totalSources;
                                              if (count === 0) return null;
                                              return (
                                                <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200" title="Sections from page index">
                                                  <CheckCircle className="w-3 h-3" />
                                                  {count} {count === 1 ? 'section' : 'sections'}
                                                </span>
                                              );
                                            })()}
                                            {/* Graph entities */}
                                            {hasGraph && message.graphContext!.entitiesFound > 0 && (
                                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200" title="Entities found in knowledge graph">
                                                <Brain className="w-3 h-3" />
                                                {message.graphContext!.entitiesFound} {message.graphContext!.entitiesFound === 1 ? 'entity' : 'entities'}
                                              </span>
                                            )}
                                            {/* Graph relationships */}
                                            {hasGraph && message.graphContext!.relationshipsTraversed > 0 && (
                                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200" title="Relationships traversed in knowledge graph">
                                                <Network className="w-3 h-3" />
                                                {message.graphContext!.relationshipsTraversed} {message.graphContext!.relationshipsTraversed === 1 ? 'relationship' : 'relationships'}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {new Date(
                                        message.timestamp
                                      ).toLocaleTimeString()}
                                    </p>
                                  </div>
                                  {message.role === "user" && (
                                    <User className="w-5 h-5 mt-1 flex-shrink-0" />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                        {isSending && (
                          <div className="flex justify-start">
                            <div className="bg-muted rounded-lg p-3">
                              <div className="flex items-center space-x-2">
                                <Bot className="w-5 h-5 text-primary" />
                                <div className="flex space-x-1">
                                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                                  <div
                                    className="w-2 h-2 bg-primary rounded-full animate-bounce"
                                    style={{ animationDelay: "0.1s" }}
                                  ></div>
                                  <div
                                    className="w-2 h-2 bg-primary rounded-full animate-bounce"
                                    style={{ animationDelay: "0.2s" }}
                                  ></div>
                                </div>
                                <span className="text-sm text-muted-foreground">
                                  AI is thinking...
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Chat Input - Fixed at bottom */}
                      <div className="p-4 border-t border-border bg-background flex-shrink-0">
                        {/* Chat Settings Toggle */}
                        <div className="mb-2">
                          <button
                            onClick={() => setShowChatSettings(!showChatSettings)}
                            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Filter className="w-3 h-3" />
                            {showChatSettings ? 'Hide' : 'Show'} Query Settings
                            <ChevronDown className={`w-3 h-3 transition-transform ${showChatSettings ? 'rotate-180' : ''}`} />
                          </button>

                          {showChatSettings && (
                            <div className="mt-2 p-3 bg-muted/50 rounded-lg border border-border space-y-3">
                              {/* Search Mode */}
                              <div className="flex items-center gap-3">
                                <label className="text-xs font-medium text-muted-foreground w-20 flex-shrink-0">Search</label>
                                <div className="flex-1 flex gap-1 bg-background border border-border rounded-lg p-0.5">
                                  {([
                                    { value: 'fast', label: 'Fast', desc: 'BM25 keyword' },
                                    { value: 'semantic', label: 'Semantic', desc: 'Vector search' },
                                    { value: 'hybrid', label: 'Hybrid', desc: 'Both combined' },
                                    { value: 'page-index', label: 'Page Index', desc: 'Structural tree retrieval' },
                                    { value: 'deep', label: 'Deep', desc: 'Hybrid + Page Index' },
                                  ] as const).map((mode) => (
                                    <button
                                      key={mode.value}
                                      onClick={() => setSearchMode(mode.value)}
                                      className={`flex-1 px-2 py-1.5 text-xs rounded-md transition-colors ${
                                        searchMode === mode.value
                                          ? 'bg-primary text-primary-foreground shadow-sm'
                                          : 'text-muted-foreground hover:text-foreground'
                                      }`}
                                      title={mode.desc}
                                    >
                                      {mode.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <p className="text-[10px] text-muted-foreground pl-[92px] -mt-2">
                                {searchMode === 'fast' && 'Keyword match — no embedding API call, instant results'}
                                {searchMode === 'semantic' && 'Embedding similarity — best accuracy, slower'}
                                {searchMode === 'hybrid' && 'BM25 + Vector combined via rank fusion — best of both'}
                                {searchMode === 'page-index' && 'LLM picks sections from document tree → exact text retrieval (requires Page Index)'}
                                {searchMode === 'deep' && 'Parallel Hybrid + Page Index, merged by synthesis LLM — highest accuracy (requires Page Index)'}
                              </p>

                              {/* Temperature */}
                              <div className="flex items-center gap-3">
                                <label className="text-xs font-medium text-muted-foreground w-20 flex-shrink-0">Temperature</label>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.1"
                                  value={chatTemperature}
                                  onChange={(e) => setChatTemperature(parseFloat(e.target.value))}
                                  className="flex-1 h-1.5 accent-primary"
                                />
                                <span className="text-xs text-muted-foreground w-8 text-right">{chatTemperature}</span>
                              </div>

                              {/* Max Tokens */}
                              <div className="flex items-center gap-3">
                                <label className="text-xs font-medium text-muted-foreground w-20 flex-shrink-0">Max Tokens</label>
                                <select
                                  value={chatMaxTokens}
                                  onChange={(e) => setChatMaxTokens(parseInt(e.target.value))}
                                  className="flex-1 px-2 py-1 text-xs bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary/50"
                                >
                                  <option value={512}>512 — Short answer</option>
                                  <option value={1024}>1024 — Medium</option>
                                  <option value={2048}>2048 — Detailed (default)</option>
                                  <option value={4096}>4096 — Long / comprehensive</option>
                                </select>
                              </div>

                              {/* Top K */}
                              <div className="flex items-center gap-3">
                                <label className="text-xs font-medium text-muted-foreground w-20 flex-shrink-0">Top K</label>
                                <input
                                  type="range"
                                  min="5"
                                  max="50"
                                  step="5"
                                  value={chatTopK}
                                  onChange={(e) => setChatTopK(parseInt(e.target.value))}
                                  className="flex-1 h-1.5 accent-primary"
                                />
                                <span className="text-xs text-muted-foreground w-8 text-right">{chatTopK}</span>
                              </div>

                              {/* Min Score */}
                              <div className="flex items-center gap-3">
                                <label className="text-xs font-medium text-muted-foreground w-20 flex-shrink-0">Min Score</label>
                                <input
                                  type="range"
                                  min="0.1"
                                  max="0.9"
                                  step="0.1"
                                  value={chatMinScore}
                                  onChange={(e) => setChatMinScore(parseFloat(e.target.value))}
                                  className="flex-1 h-1.5 accent-primary"
                                />
                                <span className="text-xs text-muted-foreground w-8 text-right">{chatMinScore}</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {chatError && (
                          <div className="mb-3 p-2 bg-destructive/10 text-destructive text-sm rounded-lg flex items-center justify-between">
                            <span>{chatError}</span>
                            <button
                              onClick={() => setChatError(null)}
                              className="text-destructive hover:text-destructive/80"
                            >
                              ✕
                            </button>
                          </div>
                        )}

                        <div className="flex space-x-3">
                          <div className="flex-1 relative">
                            <textarea
                              value={inputMessage}
                              onChange={(e) => setInputMessage(e.target.value)}
                              onKeyPress={handleKeyPress}
                              placeholder={
                                selectedDocuments.length > 0
                                  ? "Ask a question about your documents... (Shift+Enter for new line)"
                                  : "Select documents first to start chatting..."
                              }
                              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all resize-none text-sm"
                              disabled={
                                isSending || selectedDocuments.length === 0
                              }
                              rows={1}
                              style={{ minHeight: "40px", maxHeight: "80px" }}
                            />
                          </div>
                          <button
                            onClick={handleSendMessage}
                            disabled={
                              !inputMessage.trim() ||
                              isSending ||
                              selectedDocuments.length === 0
                            }
                            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Quick action buttons */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {[
                            "Summarize",
                            "Key points",
                            "Action items",
                            "Important dates",
                          ].map((action) => (
                            <button
                              key={action}
                              onClick={() => setInputMessage(`${action}: `)}
                              className="px-2 py-1 text-xs bg-muted hover:bg-muted/80 rounded-full transition-colors"
                              disabled={selectedDocuments.length === 0}
                            >
                              {action}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          {/* Graph Explorer Tab */}
          {activeTab === "graph" && graphEnabled && (
            <motion.div
              key="graph"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              <GraphExplorer
                orgId={orgId}
                userId={user?.id}
                onSwitchToChat={() => { setActiveTab("chat"); }}
                docFilter={graphDocFilter}
                onClearDocFilter={() => setGraphDocFilter(null)}
              />
            </motion.div>
          )}
          {/* Settings Tab */}
          {activeTab === "settings" && (
            <motion.div
              key="settings"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              <KBSettings orgId={orgId} userId={user?.id} isOrgAdmin={isOrgAdmin} isPlatformAdmin={isPlatformAdmin} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Reprocess Dialog */}
        {showReprocessDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Reprocess Knowledge Graph</h3>
                <button onClick={() => setShowReprocessDialog(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Re-extract entities and relationships for: <span className="font-medium">{showReprocessDialog}</span>
              </p>

              {/* Ontology mode */}
              <div className="space-y-3 mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  <Brain className="w-4 h-4 inline mr-1 text-purple-500" />
                  Ontology Mode
                </label>
                <select
                  value={reprocessOntologyMode}
                  onChange={(e) => setReprocessOntologyMode(e.target.value as 'static' | 'hybrid' | 'dynamic')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                >
                  <option value="static">Preconfigured only — uses default.json as-is</option>
                  <option value="hybrid">Preconfigured + AI append — base + AI additions</option>
                  <option value="dynamic">AI Dynamic — generates entirely from content</option>
                </select>
              </div>

              {/* Category override */}
              <div className="space-y-2 mb-4">
                <label className="block text-sm font-medium text-gray-700">Category Override (optional)</label>
                <input
                  type="text"
                  value={reprocessCategory}
                  onChange={(e) => setReprocessCategory(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                  placeholder="e.g. concussion, sports_medicine"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-xs text-gray-500">Leave empty to use document's existing category</p>
              </div>

              {/* Force new ontology */}
              <div className="flex items-center gap-2 mb-6">
                <input
                  type="checkbox"
                  id="forceOntology"
                  checked={reprocessForceOntology}
                  onChange={(e) => setReprocessForceOntology(e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="forceOntology" className="text-sm text-gray-700">
                  Force regenerate ontology (delete cached version)
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowReprocessDialog(null)}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={executeReprocess}
                  className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700"
                >
                  Reprocess
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload Dialog */}
        {showUploadDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                {/* Enhanced Header with Close Button */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 p-2 rounded-lg">
                      <Upload className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Upload Documents</h2>
                      <p className="text-sm text-gray-600">Add documents to your knowledge base</p>
                    </div>
                  </div>
                  <button
                    onClick={closeDialog}
                    className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-lg transition-colors"
                    aria-label="Close dialog"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Step 1: File Selection Zone */}
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-300 cursor-pointer ${
                    isDragActive
                      ? "border-primary bg-primary/5"
                      : stagedFiles.length > 0
                        ? "border-green-300 bg-green-50/50"
                        : "border-gray-300 hover:border-primary/60 hover:bg-gray-50"
                  } ${isUploading ? "pointer-events-none opacity-80" : ""}`}
                >
                  <input {...getInputProps()} />
                  <div className="space-y-3">
                    <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center ${
                      stagedFiles.length > 0 ? "bg-green-100" : "bg-primary/10"
                    }`}>
                      {stagedFiles.length > 0 ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <Upload className="w-6 h-6 text-primary" />
                      )}
                    </div>
                    {stagedFiles.length > 0 ? (
                      <div className="space-y-2">
                        <h3 className="text-base font-medium text-gray-900">
                          {stagedFiles.length} file{stagedFiles.length > 1 ? 's' : ''} selected
                        </h3>
                        <div className="flex flex-col gap-2 w-full max-w-xl mx-auto text-left" onClick={(e) => e.stopPropagation()}>
                          {stagedFiles.map((file, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                              <FileText className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                              <input
                                type="text"
                                value={fileCustomNames[idx] ?? file.name}
                                onChange={(e) => {
                                  const updated = [...fileCustomNames];
                                  updated[idx] = e.target.value;
                                  setFileCustomNames(updated);
                                }}
                                className="flex-1 text-xs text-gray-800 outline-none bg-transparent min-w-0"
                                placeholder="Document name"
                              />
                              <span className="text-gray-400 text-xs flex-shrink-0">({(file.size / 1024 / 1024).toFixed(1)}MB)</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">
                          Drop more files or click to replace
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <h3 className="text-base font-medium text-gray-900">
                          {isDragActive ? "Drop files here" : "Select Documents"}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Drag and drop files here, or click to browse
                        </p>
                        <p className="text-xs text-gray-500">
                          PDF, Word (.doc/.docx), Text (.txt), Markdown (.md), RTF, ODT — Max 50MB per file
                        </p>
                        {!isDragActive && !isUploading && (
                          <button
                            type="button"
                            className="px-5 py-2 bg-primary text-white rounded-md font-medium hover:bg-primary/90 transition-colors text-sm shadow-sm"
                          >
                            Choose Files
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* AI Analysis (shown after files are staged) */}
                {stagedFiles.length > 0 && uploadProviders.has('knowledge') && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">AI Document Analysis</span>
                        {isRecommending && (
                          <span className="text-xs text-blue-500 flex items-center gap-1">
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Analyzing...
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          if (isRecommending || stagedFiles.length === 0) return;
                          setIsRecommending(true);
                          try {
                            const text = await stagedFiles[0].slice(0, 4000).text();
                            const analysis = await knowledgeService.analyzeDocument(text, stagedFiles[0].name, documentCategory || undefined);
                            setChunkingRecommendation(analysis.chunking);
                            setChunkingStrategy(analysis.chunking.recommended);
                            if (analysis.category) setDocumentCategory(analysis.category);
                            if (analysis.subcategory) setDocumentSubCategory(analysis.subcategory);
                            if (analysis.tags.length > 0) setDocumentTags(analysis.tags);
                            if (analysis.ontology.entityTypes.length > 0) {
                              if (ontologyMode === 'dynamic') {
                                setRecommendedOntology(analysis.ontology);
                              } else if (ontologyMode === 'hybrid' && recommendedOntology) {
                                const existingNames = new Set(recommendedOntology.entityTypes.map(e => e.name));
                                const existingRelNames = new Set(recommendedOntology.relationshipTypes.map(r => r.name));
                                setRecommendedOntology({
                                  entityTypes: [...recommendedOntology.entityTypes, ...analysis.ontology.entityTypes.filter(e => !existingNames.has(e.name))],
                                  relationshipTypes: [...recommendedOntology.relationshipTypes, ...analysis.ontology.relationshipTypes.filter(r => !existingRelNames.has(r.name))],
                                });
                              }
                            }
                          } catch (err) {
                            console.warn('Re-analysis failed:', err);
                          } finally {
                            setIsRecommending(false);
                          }
                        }}
                        disabled={isRecommending}
                        className="text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                      >
                        Re-analyze
                      </button>
                    </div>

                    {chunkingRecommendation && (
                      <div className="space-y-3">
                        {/* Chunking recommendation */}
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-green-800">
                              Chunking: {chunkingRecommendation.recommended.charAt(0).toUpperCase() + chunkingRecommendation.recommended.slice(1)}
                            </span>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              {Math.round(chunkingRecommendation.confidence * 100)}%
                            </span>
                          </div>
                          <p className="text-xs text-green-700">{chunkingRecommendation.reasoning}</p>
                        </div>

                        {/* AI ontology summary */}
                        {recommendedOntology && recommendedOntology.entityTypes.length > 0 && (
                          <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-purple-800">
                                Ontology: {ontologyMode === 'static' ? 'Preconfigured' : ontologyMode === 'hybrid' ? 'Preconfigured + AI' : 'AI Generated'}
                              </span>
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                {recommendedOntology.entityTypes.length} entities, {recommendedOntology.relationshipTypes.length} rels
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {recommendedOntology.entityTypes.slice(0, 8).map((et) => (
                                <span key={et.name} className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">
                                  {et.name}
                                </span>
                              ))}
                              {recommendedOntology.entityTypes.length > 8 && (
                                <span className="text-xs text-purple-500">+{recommendedOntology.entityTypes.length - 8} more</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {!chunkingRecommendation && !isRecommending && (
                      <p className="text-xs text-gray-500">Recommendation not available. Configure settings manually below.</p>
                    )}
                  </div>
                )}

                {/* Step 2: Configuration (shown after files are staged) */}
                {stagedFiles.length > 0 && (
                  <>
                {/* Category + Tags */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6 mb-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 flex items-center gap-1">
                      <Filter className="w-4 h-4 text-primary" />
                      Category
                      {documentCategory && <span className="ml-1 text-xs font-normal text-primary bg-primary/10 px-1.5 py-0.5 rounded">AI</span>}
                    </label>
                    <select
                      value={documentCategory}
                      onChange={(e) => {
                        setDocumentCategory(e.target.value);
                        setDocumentSubCategory('');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
                    >
                      <option value="">— Select category —</option>
                      {loadedCategories.map((c) => (
                        <option key={c.name} value={c.name}>{c.name.replace(/_/g, ' ')}</option>
                      ))}
                      {documentCategory && !loadedCategories.find(c => c.name === documentCategory) && (
                        <option value={documentCategory}>{documentCategory.replace(/_/g, ' ')} (new)</option>
                      )}
                    </select>
                    {documentCategory && (
                      <p className="text-xs text-gray-500">AI-detected — change if needed</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      <Filter className="w-4 h-4 inline mr-1 text-primary" />
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-1 min-h-[38px] p-2 border border-gray-300 rounded-lg bg-white">
                      {documentTags.map((tag) => (
                        <span key={tag} className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          {tag}
                          <button
                            type="button"
                            onClick={() => setDocumentTags(documentTags.filter(t => t !== tag))}
                            className="hover:text-blue-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
                            e.preventDefault();
                            const newTag = tagInput.trim().toLowerCase();
                            if (!documentTags.includes(newTag)) {
                              setDocumentTags([...documentTags, newTag]);
                            }
                            setTagInput('');
                          }
                          if (e.key === 'Backspace' && !tagInput && documentTags.length > 0) {
                            setDocumentTags(documentTags.slice(0, -1));
                          }
                        }}
                        placeholder={documentTags.length === 0 ? "Type tag + Enter" : ""}
                        className="flex-1 min-w-[80px] text-sm border-none p-0 focus:ring-0 bg-transparent outline-none"
                      />
                    </div>
                    {documentTags.length > 0 && (
                      <p className="text-xs text-gray-500">{documentTags.length} tags — press Enter or comma to add</p>
                    )}
                  </div>
                </div>
                {/* Sub-category — full width row below category/tags grid */}
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700 flex items-center gap-1">
                    <Filter className="w-4 h-4 text-primary" />
                    Sub-category
                    {documentSubCategory && <span className="ml-1 text-xs font-normal text-primary bg-primary/10 px-1.5 py-0.5 rounded">AI</span>}
                  </label>
                  <select
                    value={documentSubCategory}
                    onChange={(e) => setDocumentSubCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
                  >
                    <option value="">— None —</option>
                    {(loadedCategories.find(c => c.name === documentCategory)?.subcategories || []).map((sub) => (
                      <option key={sub} value={sub}>{sub.replace(/_/g, ' ')}</option>
                    ))}
                    {documentSubCategory && !(loadedCategories.find(c => c.name === documentCategory)?.subcategories || []).includes(documentSubCategory) && (
                      <option value={documentSubCategory}>{documentSubCategory.replace(/_/g, ' ')} (new)</option>
                    )}
                  </select>
                  {documentSubCategory && (
                    <p className="text-xs text-gray-500">AI-detected — change if needed</p>
                  )}
                </div>

                {/* Ontology Mode (when graph is enabled) */}
                {graphEnabled && (
                  <div className="space-y-2 mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      <Brain className="w-4 h-4 inline mr-1 text-purple-500" />
                      Knowledge Graph Ontology
                    </label>
                    <div className="flex items-center gap-3">
                      <select
                        value={`${ontologyMode}::${selectedStaticOntology}`}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '__dynamic__') {
                            setOntologyMode('dynamic');
                            setRecommendedOntology(null);
                          } else if (val.startsWith('hybrid::')) {
                            setOntologyMode('hybrid');
                            setSelectedStaticOntology(val.replace('hybrid::', ''));
                          } else if (val.startsWith('static::')) {
                            setOntologyMode('static');
                            setSelectedStaticOntology(val.replace('static::', ''));
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      >
                        {staticOntologies.map((o) => (
                          <optgroup key={o.name} label={`${o.domain}${o.isDefault ? ' (default)' : ''}`}>
                            <option value={`static::${o.name}`}>
                              Preconfigured only — {o.entityTypeCount} entities, {o.relationshipTypeCount} rels
                            </option>
                            <option value={`hybrid::${o.name}`}>
                              Preconfigured + AI append — base {o.entityTypeCount} entities + AI additions
                            </option>
                          </optgroup>
                        ))}
                        <optgroup label="AI Generated">
                          <option value="__dynamic__">AI Dynamic — generates entirely from document</option>
                        </optgroup>
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowOntologyEditor(!showOntologyEditor)}
                        className="px-3 py-2 text-xs text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50"
                      >
                        {showOntologyEditor ? 'Hide' : 'View'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      {ontologyMode === 'static' && (
                        staticOntologies.find(o => o.name === selectedStaticOntology)?.description || 'Uses preconfigured ontology only'
                      )}
                      {ontologyMode === 'hybrid' && (
                        recommendedOntology
                          ? `${staticOntologies.find(o => o.name === selectedStaticOntology)?.entityTypeCount || 0} preconfigured + ${recommendedOntology.entityTypes.length - (staticOntologies.find(o => o.name === selectedStaticOntology)?.entityTypeCount || 0)} AI-added = ${recommendedOntology.entityTypes.length} total entities`
                          : 'Preconfigured base + AI will append new types found in document'
                      )}
                      {ontologyMode === 'dynamic' && (
                        recommendedOntology && recommendedOntology.entityTypes.length > 0
                          ? `AI generated ${recommendedOntology.entityTypes.length} entity types, ${recommendedOntology.relationshipTypes.length} relationships`
                          : 'AI will analyze document and generate ontology on upload'
                      )}
                    </p>

                    {/* Ontology viewer/editor (expanded) */}
                    {showOntologyEditor && recommendedOntology && (
                      <div className="mt-2 p-3 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
                        <div className="flex flex-wrap gap-1 mb-2">
                          {recommendedOntology.entityTypes.map((et) => (
                            <span key={et.name} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                              {et.name}
                            </span>
                          ))}
                        </div>

                        <div>
                          <span className="text-xs font-medium text-purple-800 block mb-1">Entity Types ({recommendedOntology.entityTypes.length})</span>
                          {recommendedOntology.entityTypes.map((et, idx) => (
                            <div key={idx} className="flex items-start gap-2 mb-1.5 p-2 bg-white rounded border border-purple-100">
                              <div className="flex-1 min-w-0">
                                <input
                                  type="text"
                                  value={et.name}
                                  onChange={(e) => {
                                    const updated = { ...recommendedOntology };
                                    updated.entityTypes = [...updated.entityTypes];
                                    updated.entityTypes[idx] = { ...et, name: e.target.value };
                                    setRecommendedOntology(updated);
                                  }}
                                  className="text-xs font-medium w-full border-none p-0 focus:ring-0 bg-transparent"
                                />
                                <p className="text-xs text-gray-500 truncate">{et.description}</p>
                                {et.properties && et.properties.length > 0 && (
                                  <p className="text-xs text-gray-400 truncate">Props: {et.properties.join(', ')}</p>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = { ...recommendedOntology };
                                  updated.entityTypes = updated.entityTypes.filter((_, i) => i !== idx);
                                  setRecommendedOntology(updated);
                                }}
                                className="text-red-400 hover:text-red-600 text-xs mt-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>

                        <div>
                          <span className="text-xs font-medium text-purple-800 block mb-1">Relationships ({recommendedOntology.relationshipTypes.length})</span>
                          {recommendedOntology.relationshipTypes.map((rt, idx) => (
                            <div key={idx} className="flex items-center gap-1 mb-1 text-xs text-purple-700">
                              <span className="truncate">{rt.fromType}</span>
                              <span className="text-purple-400">→</span>
                              <span className="font-medium">{rt.name}</span>
                              <span className="text-purple-400">→</span>
                              <span className="truncate">{rt.toType}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = { ...recommendedOntology };
                                  updated.relationshipTypes = updated.relationshipTypes.filter((_, i) => i !== idx);
                                  setRecommendedOntology(updated);
                                }}
                                className="text-red-400 hover:text-red-600 ml-1"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Document Description */}
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    <FileText className="w-4 h-4 inline mr-1 text-primary" />
                    Description (optional)
                  </label>
                  <textarea
                    value={documentDescription}
                    onChange={(e) => setDocumentDescription(e.target.value)}
                    placeholder="Brief description of this document..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none text-sm"
                    rows={2}
                  />
                </div>

                {/* AI Parsing Configuration (Knowledge Base provider only) */}
                {uploadProviders.has('knowledge') && (
                  <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-100 mb-4">
                    <div className="flex items-center gap-2">
                      <Brain className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Parsing & Chunking</span>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {/* Parse Tier */}
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-600">Parse Tier</label>
                        <select
                          value={parseTier}
                          onChange={(e) => setParseTier(e.target.value as typeof parseTier)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
                        >
                          <option value="fast">Fast (1 credit/page)</option>
                          <option value="cost_effective">Cost Effective (3 credits/page)</option>
                          <option value="agentic">Agentic (10 credits/page)</option>
                          <option value="agentic_plus">Agentic Plus (45 credits/page)</option>
                        </select>
                      </div>

                      {/* Language */}
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-600">Language</label>
                        <select
                          value={parseLanguage}
                          onChange={(e) => setParseLanguage(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
                        >
                          <option value="en">English</option>
                          <option value="fr">French</option>
                          <option value="de">German</option>
                          <option value="es">Spanish</option>
                          <option value="it">Italian</option>
                          <option value="pt">Portuguese</option>
                          <option value="zh">Chinese</option>
                          <option value="ja">Japanese</option>
                          <option value="ko">Korean</option>
                          <option value="ar">Arabic</option>
                        </select>
                      </div>
                    </div>

                    {/* Chunking Strategy */}
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">Chunking Strategy</label>
                      <select
                        value={chunkingStrategy}
                        onChange={(e) => setChunkingStrategy(e.target.value as ChunkingStrategy)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
                      >
                        {chunkingStrategies.map((s) => (
                          <option key={s.name} value={s.name}>
                            {s.name.charAt(0).toUpperCase() + s.name.slice(1)} — {s.bestFor}
                          </option>
                        ))}
                      </select>
                      {chunkingStrategy === 'proposition' && (
                        <p className="text-xs text-amber-600">~$0.50-2.00 per 50-page document (LLM calls).</p>
                      )}
                      {chunkingStrategy === 'semantic' && (
                        <p className="text-xs text-blue-500">Groups sentences by embedding similarity. Best for long narratives.</p>
                      )}
                    </div>

                    {/* Page Index */}
                    <div className="space-y-1">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={buildPageIndex}
                          onChange={(e) => setBuildPageIndex(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-xs font-medium text-gray-700">Build Page Index</span>
                      </label>
                      {buildPageIndex && (
                        <p className="text-xs text-blue-600 ml-6">
                          Parses section headings into a navigable tree with summaries. Enables <em>page-index</em> and <em>deep</em> retrieval modes. Adds ~20–45s per document. Best for structured PDFs with clear headings.
                        </p>
                      )}
                    </div>

                    {/* Parsing Instructions */}
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">Parsing Instructions (optional)</label>
                      <textarea
                        value={parsingInstructions}
                        onChange={(e) => setParsingInstructions(e.target.value)}
                        placeholder="e.g. Extract all numbered protocols, symptom lists, and return-to-play steps as distinct sections."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none text-sm"
                        rows={2}
                      />
                    </div>
                  </div>
                )}

                {/* Provider Selection */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Filter className="w-4 h-4 inline mr-1 text-primary" />
                    Upload To
                  </label>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={uploadProviders.has('knowledge')}
                        onChange={(e) => {
                          const newProviders = new Set(uploadProviders);
                          if (e.target.checked) {
                            newProviders.add('knowledge');
                          } else {
                            newProviders.delete('knowledge');
                          }
                          setUploadProviders(newProviders);
                        }}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
                      />
                      <span className="text-sm text-gray-700 flex items-center gap-1">
                        <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">KB</span>
                        Knowledge Base
                      </span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={uploadProviders.has('gemini')}
                        onChange={(e) => {
                          const newProviders = new Set(uploadProviders);
                          if (e.target.checked) {
                            newProviders.add('gemini');
                          } else {
                            newProviders.delete('gemini');
                          }
                          setUploadProviders(newProviders);
                        }}
                        disabled={!geminiStoreId}
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-sm text-gray-700 flex items-center gap-1">
                        <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs">Gemini</span>
                        Gemini FileSearch
                        {!geminiStoreId && <span className="text-xs text-gray-400">(N/A)</span>}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Document Visibility */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="w-4 h-4 inline mr-1 text-primary" />
                    Visibility
                  </label>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="docVisibility"
                        value="private"
                        checked={docVisibility === 'private'}
                        onChange={() => setDocVisibility('private')}
                        className="w-4 h-4 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">Private (only me)</span>
                    </label>
                    {isOrgAdmin && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="docVisibility"
                          value="org"
                          checked={docVisibility === 'org'}
                          onChange={() => setDocVisibility('org')}
                          className="w-4 h-4 text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-700">Organisation</span>
                      </label>
                    )}
                    {isPlatformAdmin && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="docVisibility"
                          value="global"
                          checked={docVisibility === 'global'}
                          onChange={() => setDocVisibility('global')}
                          className="w-4 h-4 text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-700">Global (all orgs)</span>
                      </label>
                    )}
                  </div>
                </div>

                {/* Upload Button */}
                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setStagedFiles([]);
                      setFileCustomNames([]);
                      setChunkingRecommendation(null);
                    }}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Clear Files
                  </button>
                  <button
                    type="button"
                    onClick={handleUploadStaged}
                    disabled={isUploading || isOrgIdLoading || uploadProviders.size === 0}
                    className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm shadow-sm"
                  >
                    <Upload className="w-4 h-4" />
                    {isUploading ? "Uploading..." : `Upload ${stagedFiles.length} Document${stagedFiles.length > 1 ? 's' : ''}`}
                  </button>
                </div>
                  </>
                )}

                {/* Enhanced Upload Progress */}
                {(isUploading ||
                  (uploadProgress &&
                    Object.keys(uploadProgress).length > 0)) && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/10 p-1.5 rounded-lg">
                          <Upload className="w-4 h-4 text-primary" />
                        </div>
                        <h4 className="text-sm font-medium text-gray-900">
                          Upload Progress
                        </h4>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                          {uploadProgress
                            ? Object.keys(uploadProgress).length
                            : 0}{" "}
                          file(s)
                        </div>

                        {/* Cancel Upload Button */}
                        {isUploading && (
                          <button
                            onClick={cancelUpload}
                            className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1 px-2 py-1 hover:bg-red-50 rounded-full transition-colors"
                          >
                            <X className="w-3 h-3" />
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>

                    {uploadProgress &&
                      Object.entries(uploadProgress).map(
                        ([documentId, progress]) => (
                          <div
                            key={documentId}
                            className="space-y-2 p-4 bg-gray-50 rounded-lg border border-gray-100"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <div
                                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    progress.status === "uploading"
                                      ? "bg-primary animate-pulse"
                                      : progress.status === "processing"
                                      ? "bg-yellow-500 animate-pulse"
                                      : progress.status === "success"
                                      ? "bg-green-500"
                                      : "bg-red-500"
                                  }`}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 text-sm truncate">
                                    {progress.message || "Processing..."}
                                  </p>
                                  <p className="text-xs text-gray-500 capitalize">
                                    {progress.status}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="text-sm font-medium text-gray-900">
                                  {Math.round(progress.progress || 0)}%
                                </div>
                              </div>
                            </div>

                            <Progress
                              value={progress.progress || 0}
                              className="h-2"
                            />
                          </div>
                        )
                      )}

                    {isUploading &&
                      (!uploadProgress ||
                        Object.keys(uploadProgress).length === 0) && (
                        <div className="space-y-2 p-4 bg-gray-50 rounded-lg border border-gray-100">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                              <p className="font-medium text-gray-900 text-sm">
                                Initializing upload...
                              </p>
                            </div>
                            <button
                              onClick={cancelUpload}
                              className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                            >
                              <X className="w-3 h-3" />
                              Cancel
                            </button>
                          </div>
                          <Progress value={25} className="h-2" />
                        </div>
                      )}
                  </div>
                )}

                {/* Enhanced Dialog Footer */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    {/* Upload Status */}
                    <div className="flex items-center gap-2">
                      {!isUploading &&
                        uploadProgress &&
                        Object.keys(uploadProgress).length > 0 && (
                          <div className="flex items-center gap-2">
                            {Object.values(uploadProgress).every(
                              (p) => p.status === "success"
                            ) ? (
                              <>
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span className="text-sm text-green-600 font-medium">
                                  All uploads completed successfully!
                                </span>
                              </>
                            ) : Object.values(uploadProgress).some(
                                (p) => p.status === "error"
                              ) ? (
                              <>
                                <AlertCircle className="w-4 h-4 text-red-600" />
                                <span className="text-sm text-red-600 font-medium">
                                  Some uploads failed
                                </span>
                              </>
                            ) : (
                              <>
                                <Clock className="w-4 h-4 text-yellow-600" />
                                <span className="text-sm text-yellow-600 font-medium">
                                  Upload in progress...
                                </span>
                              </>
                            )}
                          </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                      {/* Cancel Button */}
                      <button
                        onClick={closeDialog}
                        disabled={isUploading}
                        className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUploading ? "Uploading..." : "Cancel"}
                      </button>

                      {/* Close Button (when upload is complete) */}
                      {!isUploading &&
                        uploadProgress &&
                        Object.keys(uploadProgress).length > 0 &&
                        Object.values(uploadProgress).every(
                          (p) => p.status === "success" || p.status === "error"
                        ) && (
                          <button
                            onClick={closeDialog}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                          >
                            Close
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAssistant;
